/**
 * useDatatable Hook
 * 
 * Custom hook managing comprehensive datatable state and operations including data fetching,
 * filtering, sorting, searching, pagination, and UI alerts. Provides reducer pattern for
 * state management with 15+ action types for datatable operations (loading, sorting, filtering,
 * pagination). Handles loading/error states with modal messages. Implements column visibility
 * management and applied filters tracking.
 * 
 * @returns {Object} Datatable state and dispatcher
 * @returns {Object} returns.state - Current datatable state (data, filters, sort, pagination, etc)
 * @returns {Function} returns.dispatch - Dispatcher function for state updates
 */
// useReducer hook for managing the datatable's states

import { useEffect, useReducer } from "react";
import { getAppliedFilterFieldNames } from "../../utils/helpers";
import FetchingErrorModal from "../modals/data-fetching-error-modal";
import LoadingModal from "../modals/loading-modal";
import ResultsLoadingModal from "../modals/results-loading-modal";
import ResultsReadyModal from "../modals/results-ready-modal";
import SearchResultsNotFoundModal from "../modals/search-results-not-found";
import SortErrorModal from "../modals/sort-error-modal";
import SortingModal from "../modals/sorting-modal";

const reducer = (state, action) => {
  switch (action.type) {
    case "update_state": {
      return {
        ...state,
        [action.state]: action.value,
      };
    }
    // show alert when data is fetching
    case "loading": {
      return {
        ...state,
        isLoading: true,
        alert: true,
        message: <ResultsLoadingModal />,
        argument: action.argument,
      };
    }
    // change states after new data is fetched and does not involve changing page numbers
    case "data_exists_after_fetching": {
      return {
        ...state,
        tableData: action.tableData,
        page: action.page,
        message: <ResultsReadyModal id="table-data" />,
        isLoading: false,
      };
    }
    case "no_data_after_fetching": {
      return {
        ...state,
        tableData: action.tableData,
        page: action.page,
        message: <SearchResultsNotFoundModal />,
        isLoading: false,
      };
    }
    case "show_fetching_error": {
      return {
        ...state,
        message: <FetchingErrorModal />,
        isLoading: false,
      };
    }
    case "show_sorting_error": {
      return {
        ...state,
        alert: true,
        message: <SortErrorModal />,
        isLoading: false,
      };
    }
    case "close_alert": {
      return {
        ...state,
        alert: false,
        message: "",
      };
    }
    case "update_tableData": {
      return {
        ...state,
        tableData: action.tableData,
        isLoading: false,
        alert: true,
        message: <ResultsReadyModal id="table-data" />,
      };
    }
    case "reset_table": {
      return {
        ...state,
        appliedFilterFieldNames: [],
        sortConditionsArray: [],
        filterConditionsArray: [],
        alert: true,
        isLoading: true,
        message: <ResultsLoadingModal />,
        argument: {
          ...state.argument,
          currentPage: 1,
          searchTerm: "",
          selectedHeader: "",
          selectedDropdown: "",
          filter: {
            connective: "and",
            conditions: [],
            applied: false,
          },
        },
        searchApplied: false,
        searchTermApplied: "",
      };
    }

    case "search_applied": {
      return {
        ...state,
        searchApplied: true,
        searchTermApplied: action.searchTerm,
      };
    }

    case "update_state_when_using_pagination": {
      return {
        ...state,
        page: state.page,
      };
    }
    case "update_state_when_hiding_fields": {
      return {
        ...state,
        hiddenFieldNames: [...state.hiddenFieldNames, action.header],
        hideField: true,
      };
    }
    case "hide_all_fields": {
      return {
        ...state,
        hiddenFieldNames: action.value,
      };
    }
    case "unhide_field": {
      const arr = state.hiddenFieldNames.filter(
        (item) => !(item === action.value)
      );
      return {
        ...state,
        hiddenFieldNames: arr,
      };
    }
    case "unhide_all_fields": {
      return {
        ...state,
        hiddenFieldNames: [],
      };
    }
    case "update_state_when_sorting": {
      return {
        ...state,
        isLoading: true,
        argument: action.argument,
        message: <SortingModal />,
        alert: true,
        sortConditionsArray: [action.fieldObj],
      };
    }
    // add/update the list of applied fields that are being filtered
    case "update_filtered_fields_applied": {
      return {
        ...state,
        appliedFilterFieldNames: getAppliedFilterFieldNames(action.argument),
      };
    }
    // clear filtered fields
    case "clear_filtered_fields_applied": {
      return {
        ...state,
        appliedFilterFieldNames: [],
      };
    }
    // show loading alert message
    case "load_company_details": {
      return {
        ...state,
        messsage: <LoadingModal />,
        alert: true,
      };
    }
    // show expand data for expand modal
    case "show_expand_data": {
      return {
        ...state,
        expandData: action.data,
        showExpandCard: true,
      };
    }
    // show more info data for more info modal
    case "show_more_info_data": {
      return {
        ...state,
        moreInfo: action.data,
        showMoreInfoCard: true,
      };
    }
    // show expand data for expand modal
    case "close_expand_data_card": {
      return {
        ...state,
        expandData: null,
        showExpandCard: false,
      };
    }
    // show more info data for more info modal
    case "close_more_info_card": {
      return {
        ...state,
        moreInfo: null,
        showMoreInfoCard: false,
      };
    }

    case "push_array": {
      return {
        ...state,
        [action.stateArray]: [...state[action.stateArray], action.value],
      };
    }

    case "update_array": {
      return {
        ...state,
        [action.stateArray]: [
          ...state[action.stateArray].slice(0, action.index),
          action.newElement,
          ...state[action.stateArray].slice(
            action.index + 1,
            state[action.stateArray].length
          ),
        ],
      };
    }

    case "remove_item_from_array": {
      return {
        ...state,
        [action.stateArray]: [
          ...state[action.stateArray].slice(0, action.index),
          ...state[action.stateArray].slice(
            action.index + 1,
            state[action.stateArray].length
          ),
        ],
      };
    }

    case "clear_array": {
      return {
        ...state,
        [action.stateArray]: [],
      };
    }

    default: {
      return state;
    }
  }
};

const initialState = {
  // argument state is used to send to backend for processing data
  argument: {
    numOfRows: 50,
    currentPage: 1,
    searchTerm: "",
    selectedHeader: "",
    selectedDropdown: "",
    filter: {
      connective: "and",
      conditions: [],
      applied: false,
    },
  },
  page: { startNum: 1, endNum: 50, lastPage: 0 },
  tableData: {
    results: [],
    total: 0,
    lastPage: 0,
    statistics: {
      inactiveCompanies: 0,
      reasonsForShutdown: 0,
    },
  },
  message: <LoadingModal />,
  alert: true,
  isLoading: true,

  // hook for expand card modal
  showExpandCard: false,
  expandData: "",

  // hook to show pop up card when user clicks logo
  showMoreInfoCard: false,
  moreInfo: [],

  hiddenFieldNames: [],
  hideField: false,

  // hook for collecting field names that are filtered
  appliedFilterFieldNames: [],
  filterConditionsArray: [],

  // hook for collecting field names that are sorted
  sortedFieldNames: [],
  sortConditionsArray: [],

  // hook when search is applied or not
  searchApplied: false,
  searchTermApplied: "",
};

export default function useDatatable() {
  const [state, dispatch] = useReducer(reducer, initialState);

  const datatableProps = {
    state: state,
    dispatch: dispatch,
    hiddenFieldNames: state.hiddenFieldNames,
    hideField: state.hideField,
    alert: state.alert,
    message: state.message,
    expandData: state.expandData,
    showExpandCard: state.showExpandCard,
    argument: state.argument,
    page: state.page,
    tableData: state.tableData,
    isLoading: state.isLoading,
    appliedFilterFieldNames: state.appliedFilterFieldNames,
    sortedFieldNames: state.sortedFieldNames,
    showInfoDataCard: state.showInfoDataCard,
    moreInfo: state.moreInfo,
    sortConditionsArray: state.sortConditionsArray,
    filterConditionsArray: state.filterConditionsArray,
    searchApplied: state.searchApplied,
  };

  // useEffect(() => {
  //   console.log("argument object:", state.argument);
  // }, [state.argument]);

  // useEffect(() => {
  //   console.log("tableData:", state.tableData);
  // }, [state.tableData]);

  return {
    state,
    dispatch,
    datatableProps,
  };
}
